<script setup>
import { h, ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { ToastAction } from '@/components/ui/toast';
import { useRoute, useRouter } from 'vue-router';
import { getUser, globalState, getUserAsync, refreshUsage } from '@/lib/user';
import { addProject, models, setCurrentModel } from '@/lib/gen';
import EmailVerification from '@/components/auth/EmailVerification.vue';
import { i18n } from '@/i18n';
import Attach from '@/components/preview/chat/Attach.vue';
import ModelSelector from '@/components/model/ModelSelector.vue';
import SizeSelector from '@/components/create/SizeSelector.vue';
import StyleSelector from '@/components/create/StyleSelector.vue';
import ThemeSelector from '@/components/create/ThemeSelector.vue';
import LibrarySelector from '@/components/create/LibrarySelector.vue';
import CreateType from '@/components/CreateType.vue';
import AuthModal from '@/components/auth/AuthModal.vue';
import { useToast } from '@/components/ui/toast';
import ProductSelector from './ProductSelector.vue';
import { createEmptyProject } from '@/lib/api';
import CreateBtn from './CreateBtnSm.vue';
import { addAsset, imageCompressor, uploadRefImage } from '@/lib/asset';
import { isWordHTML, parseWordHTML } from '@/lib/docs';
import BatchCount from './BatchCount.vue';
import AttachPreview from '@/components/create/attach/AttachPreview.vue';
import PromptBubble from '@/components/create/PromptBubble.vue';
import Textarea from '@/components/create/Textarea.vue';
import { validateBrowserCompatibility } from '@/lib/gen/browser';
import {
  parseSeedePrompt,
  resolveAssetsContent,
  resolveDocumentContent,
  resolveSeedeReferenceImage,
} from '@/lib/gen/prompt-parser';
import { X } from 'lucide-vue-next';

const props = defineProps({
  modelValue: String,
  scene: Object,
  model: String,
  image: Object,
  scenes: Array,
  style: Object,
  chatState: Object,
  beta: {
    type: Boolean,
    default: false,
  },
  alpha: {
    type: Boolean,
    default: false,
  },
  productSelector: {
    type: Boolean,
    default: true,
  },
  simple: {
    type: Boolean,
    default: false,
  },
  disableNavigation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:model-value',
  'update:scene',
  'update:style',
  'update:image',
  'update:model',
  'update:count',
]);

const { toast } = useToast();
const router = useRouter();
const route = useRoute();
const prompt = computed({
  get: () => props.modelValue,
  set: (v) => {
    emit('update:model-value', v);
  },
});

const batchNumber = computed({
  get: () => props.chatState.count,
  set: (v) => (props.chatState.count = v),
});

watch(
  () => props.chatState.count,
  (newCount) => {
    localStorage.setItem(BATCH_CACHE_KEY, String(newCount));
  },
  { immediate: false },
);

// Watch for changes in prompt value to handle image extraction
watch(prompt, (newValue, oldValue) => {
  if (oldValue === newValue) return;
  if (newValue) {
    const result = parseSeedePrompt(newValue);
    const textChanged = result.text !== newValue;
    if (result.materials.length) {
      props.chatState.assets = [...result.materials];
    }
    if (result.images.length) {
      const img = result.images[0];
      imagePreview.value = {
        url: img.url,
        tag: img.tag,
      };
    }
    if (result.theme) {
      props.chatState.theme = result.theme;
    }
    if (result.style) {
      props.chatState.style = result.style;
    }
    if (result.documents.length) {
      props.chatState.documents = [...result.documents];
    }
    if (textChanged) {
      prompt.value = result.text;
    }
  }
});

const imagePreview = computed({
  get: () => props.image,
  set: (v) => {
    emit('update:image', v);
  },
});

const isLoading = ref(false);
const showLoginModal = ref(false);
const showEmailModal = ref(false);
const sizeRef = ref(null);
const styleRef = ref(null);
const themeRef = ref(null);
const libraryRef = ref(null);
const batchRef = ref(null);
const createTypeRef = ref(null);

// 统一管理所有面板的打开状态
const panelStates = ref({
  attach: false,
  size: false,
  theme: false,
  style: false,
  library: false,
  model: false,
  batch: false,
  createType: false,
});

// 当某个面板打开时，关闭其他面板
function openPanel(panelName) {
  Object.keys(panelStates.value).forEach((key) => {
    panelStates.value[key] = key === panelName;
  });
}

const selectedScene = computed({
  get: () => props.scene,
  set: (scene) => emit('update:scene', scene),
});
const selectedModel = computed({
  get: () => props.model,
  set: (scene) => emit('update:model', scene),
});

const currentModel = computed(() => {
  const model = selectedModel.value;
  return models.find((item) => item.value === model) || models[0];
});

let lastModel = '';

watch(
  () => props.scene,
  (scene) => {
    sizeRef.value?.setSize(scene.size);
    if (scene.preferModel) {
      lastModel = selectedModel.value;
      selectedModel.value = scene.preferModel;
    } else {
      if (lastModel) {
        selectedModel.value = lastModel;
        lastModel = '';
      }
    }
  },
);

watch(
  () => props.chatState.theme,
  (theme) => {
    themeRef.value?.setTheme(theme);
  },
);

watch(
  () => props.chatState.style,
  (style) => {
    styleRef.value?.setStyle(style);
  },
);

function usePromptExample(example) {
  props.chatState.image = null;
  props.chatState.prompt = example;
}

// Cache key for prompt
const PROMPT_CACHE_KEY = 'create:lastPrompt';
const BATCH_CACHE_KEY = 'create:batchCount';
const usage = computed(() => {
  return globalState.usage;
});

// 监听模型变化并保存到 localStorage
watch(selectedModel, (newValue) => {
  setCurrentModel(newValue);
});

// 处理粘贴事件
const handlePaste = async (event) => {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return;

  // Check if it's Word HTML content
  if (isWordHTML(clipboardData)) {
    event.preventDefault();
    const html = clipboardData.getData('text/html');

    try {
      const parsed = await parseWordHTML(html);

      // Add text to prompt if exists
      if (parsed.text) {
        const currentPrompt = prompt.value || '';
        prompt.value = currentPrompt ? `${currentPrompt}\n${parsed.text}` : parsed.text;
      }

      // Handle images
      for (const imageData of parsed.images) {
        if (imageData.file) {
          await uploadMaterialImage(imageData.file, imageData.tag);
        }
      }

      return;
    } catch (error) {
      console.error('Failed to parse Word HTML:', error);
    }
  }

  const items = clipboardData.items;
  if (!items) return;

  // 首先检查是否有文本内容
  let hasText = false;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.type.indexOf('text') !== -1) {
      hasText = true;
      break;
    }
  }

  // 如果有文本内容，优先使用文本，不处理图片
  if (hasText) {
    return;
  }

  // 如果没有文本内容，再处理图片
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.indexOf('image') !== -1) {
      event.preventDefault();
      const file = item.getAsFile();
      if (file) {
        await handlePastedImage(file);
      }
      break;
    }
  }
};

const handlePastedImage = async (file) => {
  try {
    if (props.chatState.assetTab === AssetTabTypes.material) {
      await uploadMaterialImage(file);
    } else {
      await uploadRefImage(file, handleImageUpload);
    }
  } catch (error) {
    console.error('Failed to handle pasted image:', error);
    toast({
      title: 'Error',
      description: 'Failed to upload pasted image. Please try again.',
      variant: 'destructive',
    });
  }
};

async function uploadMaterialImage(file, tag) {
  try {
    const user = getUser();
    if (!user) return;
    const img = await imageCompressor(file, { maxSize: 1920 });
    const asset = await addAsset(img.dataURL, {
      filename: file.name,
      source: 'material',
    });
    if (tag) asset.tag = tag;
    props.chatState.assets.unshift(asset);
  } catch (error) {
    console.error('Failed to upload image:', error);
  }
}

onMounted(() => {
  // Initialize prompt from localStorage if exists
  prompt.value = localStorage.getItem(PROMPT_CACHE_KEY) || '';
  getUserAsync();
  refreshUsage();
  sizeRef.value?.setSize(props.scene?.size);

  const savedCount = localStorage.getItem(BATCH_CACHE_KEY);
  if (savedCount) {
    const count = parseInt(savedCount, 10);
    if (!isNaN(count) && count > 0) {
      props.chatState.count = count;
    }
  }

  // Show promo tooltip after 2 seconds to ensure page is loaded
  setTimeout(() => {
    showPromoTooltip.value = true;
    fullPromoText.value = getDefaultPromoText();

    // Start typewriter animation after height animation (400ms)
    setTimeout(() => {
      typewriterText();
    }, 400);

    // Change promo tooltip style 3 seconds after it appears
    setTimeout(() => {
      promoTooltipHighlight.value = false;
    }, 5000);
  }, 2000);
});

onUnmounted(() => {
  // Clean up typewriter interval on component unmount
  if (typewriterInterval) {
    clearInterval(typewriterInterval);
  }
});

watch(prompt, (newValue) => {
  localStorage.setItem(PROMPT_CACHE_KEY, newValue);
});

const handleImageUpload = async (img) => {
  imagePreview.value = img;
};

function authCheck() {
  const user = getUser();
  if (!user) {
    // Report to GA
    if (window.gtag) {
      window.gtag('event', 'require_login', {
        event_category: 'user',
        event_label: 'generate_design',
        value: 1,
      });
    }
    showLoginModal.value = true;
    return true;
  }
}

async function preCheck() {
  const user = getUser();
  if (authCheck()) return true;
  if (!user.email_verified) {
    await getUserAsync();
    if (!globalState.userInfo.verified) {
      showEmailModal.value = true;
      return true;
    }
  }

  if (props.chatState.documents.filter((asset) => asset.parsed === false).length) {
    return true;
  }

  await refreshUsage();

  const usageValue = globalState.usage;
  // Skip credit check for gemini-3-pro model
  if (props.chatState.model !== 'gemini-3-pro' && usageValue.used >= usageValue.total) {
    refreshUsage();
    toast({
      variant: 'warning',
      title: i18n.get('create.insufficientCredits.title'),
      description: i18n.get('create.insufficientCredits.description'),
      action: h(
        ToastAction,
        {
          altText: i18n.get('profile.buyCredits'),
          onClick: () => {
            router.push('/invite');
          },
        },
        {
          default: () => i18n.get('profile.buyCredits'),
        },
      ),
    });
    return true;
  }

  try {
    await validateBrowserCompatibility();
  } catch (compatibilityError) {
    console.error('Browser compatibility check failed:', compatibilityError);

    // Report to Sentry
    if (window.Sentry) {
      window.Sentry.captureException(compatibilityError, {
        tags: {
          category: 'browser_compatibility',
          feature: 'create_design',
        },
      });
    }

    // 国际化错误消息
    const localizedMessage =
      i18n.get('browserCompatibility.error.title') || 'Browser Compatibility Error';
    const localizedDescription =
      i18n.get('browserCompatibility.error.description') ||
      "Your browser doesn't support required features.";

    toast({
      title: localizedMessage,
      description: localizedDescription,
      variant: 'destructive',
      action: h(
        ToastAction,
        {
          altText: i18n.get('browserCompatibility.error.checkDetails') || 'Check compatibility',
          onClick: () => {
            router.push('/tool/browser');
          },
        },
        {
          default: () =>
            i18n.get('browserCompatibility.error.checkDetails') || 'Check compatibility',
        },
      ),
    });
    return true;
  }

  return false;
}

async function handleBatchCheck() {
  handleSubmit({ batch: true });
}

async function handlePromptCheck() {
  const image = imagePreview.value;
  if (image && image.loading) {
    return;
  }
  handleSubmit();
}

async function handleSubmit() {
  if (!prompt.value.trim() && !imagePreview.value) {
    toast({
      title: i18n.get('create.inputRequiredTitle'),
      description: i18n.get('create.inputRequiredDesc'),
    });
    return;
  }

  let promptValue = prompt.value;
  try {
    isLoading.value = true;
    if (await preCheck()) return;

    // Report to GA
    if (window.gtag) {
      window.gtag('event', 'generate_design', {
        event_category: 'design',
        event_label: prompt.value.substring(0, 100), // First 100 chars of prompt
        value: 1,
      });
    }

    const size = sizeRef.value.getSize();
    const style = styleRef.value?.getStyle();
    const themeContent = themeRef.value?.getTheme() || '';
    const libraryContent = libraryRef.value?.getLibrary();
    let value = prompt.value;
    const scene = selectedScene.value;
    let systemPrompt = scene.systemPrompt || '';

    prompt.value = '';
    nextTick(() => scrollToTop());
    const projectData = createEmptyProject(size);
    const data = {
      name: '',
      description: ``,
      data: projectData,
      meta: {
        tag: scene?.tag,
      },
      visibility: createTypeRef.value?.getValue() || 'public',
      thumbnailData: '',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    let inputPrompt = value;
    if (style) {
      inputPrompt += `${style}`;
    }

    if (themeContent) {
      inputPrompt += `\n${themeContent}`;
    }

    if (libraryContent) {
      inputPrompt += `\n${libraryContent}`;
    }

    const referenceImage = imagePreview.value;
    let referenceImageUrl = '';

    if (referenceImage) {
      if (referenceImage.url) {
        referenceImageUrl = referenceImage.url;
      } else {
        const asset = await addAsset(referenceImage.dataURL, {
          filename: referenceImage.filename,
          source: 'import',
        });
        referenceImageUrl = asset.url;
      }
      inputPrompt += resolveSeedeReferenceImage([
        {
          url: referenceImageUrl,
          tag: referenceImage.tags ? `${referenceImage.tags}` : '',
        },
      ]);
    }

    let assetsContent = resolveAssetsContent(props.chatState.assets);
    if (assetsContent) {
      inputPrompt += `\n${assetsContent}`;
    }

    let docContent = resolveDocumentContent(props.chatState.documents);
    if (docContent) {
      inputPrompt += `\n${docContent}`;
    }

    const artifactMode = scene.artifactMode || route.query.artifactMode || 'react';
    const isSvg = artifactMode === 'svg';

    if (isSvg) {
      systemPrompt += '\n{{seede_artifact:svg}}';
    }

    let model = selectedModel.value;
    const currentModelValue = currentModel.value;
    if (referenceImageUrl && !currentModelValue.supportImage) {
      const defaultModel = models[2];
      model = defaultModel.value;
    }
    const batchCount = scene.disableBatchMode ? 1 : batchNumber.value;

    const language = i18n.locale;

    window.seedePreviewPayload = {
      from: route.path,
      artifactMode: artifactMode,
      generateMode: scene.category || 'Fast',
      projectType: createTypeRef.value?.getValue() || 'public',
      prompt: inputPrompt,
      systemPrompt: systemPrompt,
      model: model,
      size,
      w: size.w,
      h: size.h,
      language: language,
      height: size.height,
      sceneApp: scene,
      sceneName: scene.name,
      scene: scene.tag,
      batchCount,
    };

    const newProject = await addProject(data);
    await import('@/views/Design.vue');
    router.push({
      name: 'Design',
      params: {
        id: newProject.id,
      },
    });
  } catch (e) {
    console.error(e);
    prompt.value = promptValue;
    toast({
      title: i18n.get('create.projectCreationFailed'),
      description: e.message || i18n.get('create.projectCreationErrorDesc'),
      variant: 'destructive',
      action: h(
        ToastAction,
        {
          altText: 'Try Again',
          onClick: () => handleSubmit(),
        },
        { default: () => i18n.get('create.tryAgain') },
      ),
    });
  } finally {
    isLoading.value = false;
  }
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

function afterAuth() {
  getUserAsync();
  refreshUsage();
  showLoginModal.value = false;
}

const AssetTabTypes = {
  reference: 'reference',
  material: 'material',
};

// Promo tooltip state
const showPromoTooltip = ref(false);
const promoTooltipHighlight = ref(true);
const promoText = ref('');
const showCursor = ref(true);
const fullPromoText = ref('');
const isHoveringGemini = ref(false);
let typewriterInterval = null;
const getDefaultPromoText = () => i18n.get('modelSelector.promoDefault');
const getGeminiPromoText = () => i18n.get('modelSelector.promoGemini');

function closePromoTooltip() {
  showPromoTooltip.value = false;
}

// Handle model hover to change promo text
function handleModelHover(model) {
  if (!showPromoTooltip.value) return;

  if (model && model.value === 'gemini-3-pro') {
    // Hovering Gemini 3 Pro - show special text
    if (!isHoveringGemini.value) {
      isHoveringGemini.value = true;
      fullPromoText.value = getGeminiPromoText();
      promoText.value = '';
      showCursor.value = true;
      typewriterText();
    }
  } else {
    // Not hovering Gemini 3 Pro (hovering other models or leaving) - restore default text
    if (isHoveringGemini.value) {
      isHoveringGemini.value = false;
      fullPromoText.value = getDefaultPromoText();
      promoText.value = '';
      showCursor.value = true;
      typewriterText();
    }
  }
}

// Typewriter animation
const typewriterText = () => {
  // Clear any existing typewriter animation
  if (typewriterInterval) {
    clearInterval(typewriterInterval);
  }

  let index = 0;

  typewriterInterval = setInterval(() => {
    if (index <= fullPromoText.value.length) {
      promoText.value = fullPromoText.value.substring(0, index);
      index++;
    } else {
      clearInterval(typewriterInterval);
      typewriterInterval = null;
      // Hide cursor after typing is complete
      setTimeout(() => {
        showCursor.value = false;
      }, 500);
    }
  }, 10); // 10ms per character for fast typing effect (5x faster)
};
</script>
<template>
  <!-- Chat Input -->
  <div class="relative mb-6">
    <!-- Login Modal -->
    <AuthModal v-model:show="showLoginModal" @after-auth="afterAuth" />
    <EmailVerification v-model:show="showEmailModal" />

    <ProductSelector
      v-if="productSelector"
      :alpha="alpha"
      :scenes="scenes"
      :disable-navigation="disableNavigation"
      v-model="selectedScene"
    />

    <div
      :class="[
        'relative rounded-[24px] border border-black/10 hover:border-black/15 overflow-y-auto overflow-x-hidden bg-white pb-10 transition-all shadow-[0_1px_3px_0px_rgba(0,0,0,0.03)]',
        isLoading && 'loading-box-breathing',
      ]"
    >
      <!-- Loading dialog -->
      <div v-if="isLoading" class="absolute inset-0 flex flex-col items-center justify-center z-20">
        <div class="flex items-center gap-2">
          <div
            class="w-5 h-5 border-2 border-black/20 border-t-black/80 rounded-full animate-spin"
          ></div>
          <p class="text-black/80 font-medium text-lg tracking-tight">
            {{ $t('create.generating') || 'Generating...' }}
          </p>
        </div>
        <p class="text-black/50 text-xs mt-2 max-w-[200px] text-center leading-relaxed">
          {{ $t('create.promptLoading') }}
        </p>
      </div>

      <!-- Loading Effect with gradient inner shadow -->
      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-lg z-10 loading-inner-shadow"
      ></div>

      <!-- Image Upload Preview -->
      <AttachPreview v-model:image="imagePreview" :chatState="chatState" />

      <Textarea v-model="prompt" :loading="isLoading" :scene="scene" @paste="handlePaste" />

      <div class="absolute pb-3 py-2 bottom-0 px-3 right-0 left-0 flex flex-row sm:flex-row gap-2">
        <!-- Model and Size Selector -->
        <div class="flex items-end gap-1 scrollbar-hide">
          <Attach
            class="h-8 rounded-full sm:h-9 bg-white text-black/60 border-[0.5px] border-black/5 hover:bg-black/5 hover:border-black/20 focus:ring-0 focus:outline-none"
            :editorState="chatState"
            :authCheck="authCheck"
            @change="handleImageUpload"
            v-model:open="panelStates.attach"
            @update:open="(val) => val && openPanel('attach')"
          />

          <SizeSelector
            ref="sizeRef"
            class="w-min h-8 rounded-full sm:h-9 bg-white text-black/60 border-[0.5px] border-black/5 hover:bg-black/5 hover:border-black/20 focus:ring-0 focus:outline-none"
            v-model:open="panelStates.size"
            @update:open="(val) => val && openPanel('size')"
          />
          <ThemeSelector
            ref="themeRef"
            class="w-min h-8 rounded-full sm:h-9 bg-white hover:bg-black/5 hover:border-black/20 text-black/60 border-[0.5px] border-black/5 focus:ring-0 focus:outline-none"
            v-model:open="panelStates.theme"
            @update:open="(val) => val && openPanel('theme')"
          />
          <StyleSelector
            ref="styleRef"
            v-if="scene?.styles"
            :scene="scene"
            :styles="scene?.styles"
            class="w-min h-8 rounded-full sm:h-9 bg-white hover:bg-black/5 hover:border-black/20 text-black/60 border-[0.5px] border-black/5 focus:ring-0 focus:outline-none"
            v-model:open="panelStates.style"
            @update:open="(val) => val && openPanel('style')"
          />
          <LibrarySelector
            v-if="beta"
            ref="libraryRef"
            class="w-min h-8 rounded-full sm:h-9 bg-white hover:bg-black/5 hover:border-black/20 text-black/60 border-[0.5px] border-black/5 focus:ring-0 focus:outline-none"
            v-model:open="panelStates.library"
            @update:open="(val) => val && openPanel('library')"
          />
        </div>

        <div class="flex ml-auto">
          <div class="hidden sm:flex items-end mr-2 gap-1">
            <ModelSelector
              v-if="!simple"
              v-model="selectedModel"
              class="w-min rounded-full h-8 sm:h-9 shadow-none bg-white hover:bg-black/5 hover:border-black/20 text-black/60 border-[0.5px] border-black/5 focus:ring-0 focus:outline-none px-1.5"
              v-model:open="panelStates.model"
              @update:open="(val) => val && openPanel('model')"
              @hover-model="handleModelHover"
            />
            <BatchCount
              v-if="!simple && !scene.disableBatchMode"
              v-model:count="batchNumber"
              ref="batchRef"
              v-model:open="panelStates.batch"
              @update:open="(val) => val && openPanel('batch')"
            />
            <CreateType
              ref="createTypeRef"
              :isPro="usage.total > 20"
              class="w-min bg-white h-8 sm:h-9 rounded-full hover:bg-black/5 hover:border-black/20 outline-0 ring-0 shadow-none border-[0.5px] border-black/5 focus:ring-0 focus:outline-none pr-1"
              v-model:open="panelStates.createType"
              @update:open="(val) => val && openPanel('createType')"
            />
          </div>

          <CreateBtn
            :count="batchNumber"
            :model="selectedModel"
            :isLoading="isLoading"
            @single="handlePromptCheck"
            @batch="handleBatchCheck"
          />
        </div>
      </div>
    </div>

    <!-- Promo Tooltip Bar - Below AI Container -->
    <Transition
      enter-active-class="promo-enter-active"
      leave-active-class="transition-all duration-300 ease-in"
      enter-from-class="promo-enter-from"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="showPromoTooltip"
        :class="[
          'flex items-center justify-between pl-3 pr-1 text-sm rounded-bl-[12px] rounded-br-[12px] mx-3 transition-all duration-500 overflow-hidden',
          promoTooltipHighlight ? 'text-white' : 'text-black/80',
        ]"
        :style="
          promoTooltipHighlight
            ? 'background: linear-gradient(90deg, #8229FF 0%, #FFC35B 100%);'
            : 'background: #ffffff; border-left: 1px solid #f0f0f0; border-right: 1px solid #f0f0f0; border-bottom: 1px solid #f0f0f0;'
        "
      >
        <span class="py-1 whitespace-nowrap">
          {{ promoText }}<span v-if="showCursor" class="typewriter-cursor">|</span>
        </span>
        <button
          @click="closePromoTooltip"
          class="ml-4 p-1 hover:bg-black/5 rounded-md transition-colors flex-shrink-0"
        >
          <X
            :class="[
              'w-4 h-4 transition-colors duration-500',
              promoTooltipHighlight
                ? 'text-white/80 hover:text-white'
                : 'text-black/60 hover:text-black/80',
            ]"
          />
        </button>
      </div>
    </Transition>

    <PromptBubble :scene="scene" @click="usePromptExample" />
  </div>
</template>

<style scoped>
@keyframes promo-height-expand {
  0% {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  100% {
    max-height: 100px;
    opacity: 1;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }
}

@keyframes cursor-blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

@keyframes breathing {
  0%,
  100% {
    box-shadow:
      inset 0 0 20px 0 rgba(43, 32, 246, 0.6),
      inset 0 0 40px 0 rgba(41, 98, 255, 0.3);
  }
  50% {
    box-shadow:
      inset 0 0 30px 0 rgba(246, 118, 32, 0.3),
      inset 0 0 60px 0 rgba(251, 255, 41, 0.3);
  }
}

.promo-enter-from {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.promo-enter-active {
  animation: promo-height-expand 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.typewriter-cursor {
  display: inline-block;
  animation: cursor-blink 200ms step-end infinite;
  margin-left: 2px;
}

.loading-box-breathing {
  animation: breathing 2s ease-in-out infinite;
}

.loading-inner-shadow {
  box-shadow:
    inset 0 0 30px 0 rgba(32, 246, 218, 0.1),
    inset 0 0 60px 0 rgba(130, 41, 255, 0.05);
}
</style>
